<template>
    <!-- Linha bloqueada fica visível (o operador entende por que não pode abrir) e sem
         ação; o motivo está no tooltip do badge do inbox. -->
    <li
        class="contact-search-item overflow-hidden"
        :class="{ 'is-bloqueado': !!linha.bloqueio, 'anima-entrada': atrasoEntrada != null, 'anima-saida': linha.saindo }"
        :style="atrasoEntrada != null && !linha.saindo ? { animationDelay: `${atrasoEntrada}ms` } : undefined"
        :aria-disabled="!!linha.bloqueio || linha.saindo"
        @click="linha.bloqueio || linha.saindo ? null : $emit('select', linha.contato)"
    >
        <a class="contact-search-link d-flex align-items-center w-100" :class="linha.bloqueio ? 'cursor-forbidden' : 'cursor-pointer'">
            <div class="me-3 flex-shrink-0">
                <AvatarField
                    :foto="linha.contato.imagem"
                    class="avatar-xs"
                    style="width: 48px !important; height: 48px !important;"
                    loading="lazy"
                />
            </div>

            <div class="flex-grow-1 overflow-hidden">
                <h5 class="text-truncate font-size-15 mb-0 contact-search-name">
                    <TextoDestacado :trechos="trechosNome" />
                </h5>

                <small class="text-truncate text-muted d-block font-size-12 mt-1">
                    <template v-if="isInstagramContato(linha.contato.tipo)">
                        <i class="ri-instagram-line me-1"></i><TextoDestacado :trechos="trechosHandle" />
                    </template>
                    <template v-else-if="isFacebookContato(linha.contato.tipo)">
                        <i class="ri-messenger-line me-1"></i>Messenger
                    </template>
                    <template v-else>
                        <i class="ri-whatsapp-line me-1"></i><TextoDestacado :trechos="trechosNumero" />
                    </template>
                </small>

                <div v-if="linha.inboxesEmAtendimento.length" class="d-flex flex-wrap gap-1 mt-1 overflow-hidden">
                    <VTooltip
                        v-for="inbox in linha.inboxesEmAtendimento"
                        :key="inbox.id ?? ''"
                        theme="whiz-rich"
                        class="tt-anchor badge-anchor"
                    >
                        <span class="badge-em-atendimento text-truncate" :class="{ 'is-bloqueado': inbox.bloqueado }">
                            <i class="me-1" :class="inbox.bloqueado ? 'ri-lock-line' : 'ri-chat-3-line'"></i>{{ inbox.nome }}<template v-if="inbox.atendidoPor"> · {{ inbox.atendidoPor }}</template>
                        </span>

                        <!-- `--tt-accent` pinta ícone, nome do inbox e selo: verde quando o
                             usuário pode entrar, cinza quando o atendimento é de outro. -->
                        <template #popper>
                            <div class="tt-card" :style="{ '--tt-accent': inbox.bloqueado ? 'var(--bs-secondary-color)' : 'var(--bs-success)' }">
                                <div class="tt-title">
                                    <i class="ri-inbox-2-fill"></i>
                                    <span>Em atendimento no inbox <span class="tt-accent-text">{{ inbox.nome }}</span></span>
                                </div>
                                <div class="tt-body">
                                    <template v-if="inbox.atendentes.length">
                                        <i class="ri-user-3-line me-1"></i>Atendente{{ inbox.atendentes.length > 1 ? 's' : '' }}:
                                        <span class="tt-strong">{{ rotuloAtendentes(inbox) }}</span>
                                    </template>
                                    <template v-else-if="inbox.isBot">
                                        <i class="ri-robot-line me-1"></i>Conduzido por bot, sem atendente.
                                    </template>
                                    <template v-else>
                                        <i class="ri-time-line me-1"></i>Em espera, sem atendente.
                                    </template>
                                </div>
                                <div v-if="inbox.bloqueado" class="tt-body">
                                    Você não tem permissão para ver atendimentos de outros atendentes.
                                </div>
                                <span class="tt-chip">{{ inbox.bloqueado ? 'Sem acesso' : 'Você pode entrar' }}</span>
                            </div>
                        </template>
                    </VTooltip>
                </div>
            </div>

            <i v-if="linha.bloqueio" class="ri-forbid-line text-muted font-size-18 ms-2 flex-shrink-0"></i>
            <i v-else class="ri-user-voice-line text-muted font-size-18 ms-2 flex-shrink-0 contact-search-action" v-tooltip="'Abrir atendimento'"></i>
        </a>
    </li>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, PropType } from 'vue';
import AvatarField from '@/components/global/AvatarField.vue';
import { ResponseContato } from '@/services/contatos/contatos';
import type { InboxEmAtendimento, LinhaContatoBusca } from '@/composables/useBuscaContatosLista';
import { destacarBusca, TrechoDestacado } from '@/utils/destacarBusca';
import { nomesDosAtendentes } from '@/utils/atendimentoAbertoDeOutro';
import { atrasoNaMontagem } from '@/utils/animacaoEntrada';
import { formatPhone } from '@/utils/phone';
import { instagramHandle, isFacebookContato, isInstagramContato } from '@/utils/instagram-contato';

const props = defineProps({
    linha: { type: Object as PropType<LinhaContatoBusca>, required: true },
    termo: { type: String, default: '' },
});

defineEmits({
    select(contato: ResponseContato) { return !!contato },
});

/** Trechos renderizados como texto + `<mark>`, sem `v-html` (nome vem do contato). */
const TextoDestacado = defineComponent({
    props: { trechos: { type: Array as PropType<TrechoDestacado[]>, required: true } },
    setup: (p) => () => p.trechos.map(t => t.destaque ? h('mark', { class: 'busca-destaque' }, t.texto) : t.texto),
});

/** "Maria Souza", "Maria e você" — o próprio usuário aparece como "você". */
function rotuloAtendentes(inbox: InboxEmAtendimento) {
    return nomesDosAtendentes(inbox.atendentes.map(a => ({ nome: a.ehVoce ? 'você' : a.nome })));
}

/**
 * Decidido na MONTAGEM, contra o relógio de agora: a linha remontada pelo scroll
 * depois da janela da cascata entra seca, sem repetir a animação.
 */
const atrasoEntrada = atrasoNaMontagem(props.linha.entrada);

const trechosNome = computed(() => destacarBusca(props.linha.contato.nome, props.termo));
const trechosHandle = computed(() => destacarBusca(instagramHandle(props.linha.contato.nome), props.termo));

/**
 * O número exibido é o formatado, mas o servidor casou o `codigo` CRU. Para BR com
 * 8 dígitos o `formatPhone` insere o 9º dígito, e aí o formatado não contém mais os
 * dígitos que casaram: nesse caso mostramos o código cru, que é onde o casamento está.
 */
const trechosNumero = computed(() => {
    const codigo = props.linha.contato.codigo ?? '';
    const formatado = formatPhone(codigo) ?? codigo;
    const noFormatado = destacarBusca(formatado, props.termo);
    if (noFormatado.some(t => t.destaque)) return noFormatado;

    const noCru = destacarBusca(codigo, props.termo);
    return noCru.some(t => t.destaque) ? noCru : noFormatado;
});
</script>

<style scoped>
.contact-search-item {
    margin-bottom: 2px;
    list-style: none;
}

.contact-search-link {
    padding: 12px 10px;
    border-radius: 12px;
    text-decoration: none !important;
    transition: background-color 0.2s ease;
    border: 1px solid transparent;
    height: 86px;
}

.contact-search-item:hover .contact-search-link {
    background-color: var(--bs-light);
}

.contact-search-item.is-bloqueado .contact-search-link {
    opacity: 0.5;
}
.contact-search-item.is-bloqueado:hover .contact-search-link {
    background-color: transparent;
}
.cursor-forbidden {
    cursor: not-allowed !important;
}

.contact-search-name {
    color: var(--bs-heading-color);
}

.contact-search-action {
    opacity: 0;
    transition: opacity 0.2s ease;
}
.contact-search-item:hover .contact-search-action {
    opacity: 1;
}
@media (hover: none) {
    .contact-search-action { opacity: 1; }
}

.badge-em-atendimento {
    max-width: 140px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.2;
    padding: 2px 8px;
    border-radius: 10px;
    background-color: rgba(var(--bs-success-rgb), 0.12);
    color: var(--bs-success);
}
.badge-em-atendimento.is-bloqueado {
    max-width: 220px;
    background-color: rgba(var(--bs-secondary-rgb), 0.15);
    color: var(--bs-secondary-color);
}

.badge-anchor {
    min-width: 0;
    max-width: 100%;
}
.badge-anchor .badge-em-atendimento {
    display: inline-block;
    cursor: help;
}

/* Entrada em cascata e saída com fade. `both` segura a linha invisível durante o
   atraso — sem ele ela piscaria visível antes de começar a animar. */
.anima-entrada {
    animation: contato-entra 0.28s ease-out both;
}
.anima-saida {
    animation: contato-sai 0.18s ease-in forwards;
    pointer-events: none;
}
@keyframes contato-entra {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
}
@keyframes contato-sai {
    from { opacity: 1; }
    to { opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
    .anima-entrada, .anima-saida { animation-duration: 0.01ms; }
}

:deep(.busca-destaque) {
    padding: 0;
    border-radius: 2px;
    background-color: rgba(var(--bs-warning-rgb), 0.35);
    color: inherit;
    font-weight: 700;
}
[data-bs-theme="dark"] :deep(.busca-destaque) {
    background-color: rgba(var(--bs-warning-rgb), 0.3);
}
</style>
